import { showConfirm } from './ConfirmDialog';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeOffline,
  getOfflineState,
  flushPending,
  clearOfflineData,
  type OfflineState,
} from '../lib/offline';

/**
 * Offline banner shown across every page when the app is offline or has
 * queued mutations waiting for sync. Rendered once in App.tsx at the top of
 * the main content.
 *
 * - Offline: an amber banner explains the offline behaviour and that edits are
 *   saved locally and replayed when back online ("Sync now" forces a replay).
 * - Just reconnected with a non-empty queue: it auto-syncs and shows a green
 *   "all changes synced" confirmation once the queue is empty.
 */
export default function OfflineBadge() {
  const { t } = useTranslation();
  const [state, setState] = useState<OfflineState>(() => getOfflineState());
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const wasOfflineRef = useRef(state.offline);

  useEffect(() => subscribeOffline(setState), []);

  // When we transition offline→online and had pending changes, auto-sync then
  // surface a "synced" confirmation once the queue is empty.
  useEffect(() => {
    if (wasOfflineRef.current && !state.offline && state.pendingCount > 0) {
      setSyncing(true);
      flushPending().finally(() => {
        setSyncing(false);
        // After a flush the pendingCount will have been updated by the store's
        // emit(); check it again once the dust settles to confirm success.
        setTimeout(() => {
          const s = getOfflineState();
          setJustSynced(s.pendingCount === 0);
          setTimeout(() => setJustSynced(false), 6000);
        }, 400);
      });
    }
    wasOfflineRef.current = state.offline;
  }, [state.offline, state.pendingCount]);

  // "Sync now" click: flush and confirm when done.
  async function onSync() {
    setSyncing(true);
    setJustSynced(false);
    try {
      await flushPending();
      const s = getOfflineState();
      setJustSynced(s.pendingCount === 0);
      if (s.pendingCount === 0) setTimeout(() => setJustSynced(false), 6000);
    } finally {
      setSyncing(false);
    }
  }

  async function onClearPending() {
    if (!(await showConfirm(t('offline.clearConfirm')))) return;
    await clearOfflineData();
    setJustSynced(false);
  }

  const hasPending = state.pendingCount > 0;
  const show = state.offline || hasPending;

  if (!show && !justSynced) return null;

  // Green "synced" confirmation (replaces the banner briefly).
  if (justSynced && !state.offline && !hasPending) {
    return (
      <div className="offline-banner offline-banner-success" role="status" aria-live="polite">
        <span className="offline-banner-icon">✅</span>
        <div className="offline-banner-body">
          <strong>{t('offline.allSynced')}</strong>
          <span className="offline-banner-sub">{t('offline.syncedSub')}</span>
        </div>
      </div>
    );
  }

  const offline = state.offline;
  return (
    <div className={`offline-banner ${offline ? 'offline-banner-offline' : 'offline-banner-syncing'}`} role="status" aria-live="polite">
      <span className="offline-banner-icon">{offline ? '📡' : '⏳'}</span>
      <div className="offline-banner-body">
        {offline ? (
          <>
            <strong>
              {t(state.pendingCount === 1 ? 'offline.offlineSaved' : 'offline.offlineSaved_other', { count: state.pendingCount })}
            </strong>
            <span className="offline-banner-sub">
              {t('offline.offlineSub')}
            </span>
          </>
        ) : (
          <>
            <strong>
              {syncing ? t('offline.syncingChanges') : t(state.pendingCount === 1 ? 'offline.waitingSync' : 'offline.waitingSync_other', { count: state.pendingCount })}
            </strong>
            <span className="offline-banner-sub">
              {t('offline.backOnline')}
            </span>
          </>
        )}
      </div>
      {hasPending && (
        <div className="offline-banner-actions">
          <button
            className="offline-badge-sync"
            onClick={onSync}
            disabled={syncing}
          >
            {syncing ? t('offline.syncing') : t('offline.syncNow')}
          </button>
          <button
            className="offline-badge-sync offline-badge-clear"
            onClick={onClearPending}
            title={t('offline.clearTitle')}
          >
            {t('offline.clear')}
          </button>
        </div>
      )}
    </div>
  );
}
